import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { supabase } from './lib/supabase';
import { carregarPerfil, moduloPermitido } from './lib/permissoes';
import { useSessaoGuard, MOTIVO_LOGOUT_LABELS } from './lib/useSessaoGuard';
import {
  AlertTriangle, Lock, LayoutGrid, Package,
  Cpu, ArrowRight, Truck, FolderOpen,
  Warehouse, Users, LogOut,
} from 'lucide-react';
import './App.css';
import './NavDrawer.css';

// Lazy loading dos módulos externos — carregados apenas quando acessados
const NotasFiscaisProprias = lazy(() => import('./NotasFiscaisProprias'));
const NotasServico         = lazy(() => import('./NotasServico'));
const GuiaPlanoContas      = lazy(() => import('./GuiaPlanoContas'));
const FinPlanoContas       = lazy(() => import('./FinPlanoContas'));
const FinReceitasDespesas  = lazy(() => import('./FinReceitasDespesas'));
const FinDRE               = lazy(() => import('./FinDRE'));
const CustoOperacao        = lazy(() => import('./CustoOperacao'));
const FinContasBancos      = lazy(() => import('./FinContasBancos'));
const FluxoCaixaProjetado  = lazy(() => import('./FluxoCaixaProjetado'));
const RelatorioContabilidade = lazy(() => import('./RelatorioContabilidade'));
const RelatorioConferenciaNotas = lazy(() => import('./RelatorioConferenciaNotas'));
const ConveniosApp         = lazy(() => import('./ConveniosApp'));
const Acondicionamento     = lazy(() => import('./Acondicionamento'));
const Parceiros            = lazy(() => import('./Parceiros'));
const CRMParceiros         = lazy(() => import('./CRMParceiros'));
const ContaColaboradores   = lazy(() => import('./ContaColaboradores'));
const OrdensServico        = lazy(() => import('./OrdensServico'));
const EstoqueRecipientes   = lazy(() => import('./EstoqueRecipientes'));
const NavDrawer            = lazy(() => import('./NavDrawer'));
const ControleAtendimentos = lazy(() => import('./ControleAtendimentos'));
const ControleCaixa        = lazy(() => import('./ControleCaixa'));
const ComposicaoProduto    = lazy(() => import('./ComposicaoProduto'));
const RastreioPublico      = lazy(() => import('./RastreioPublico'));
const ControleAcesso       = lazy(() => import('./ControleAcesso'));
const LogsAuditoria        = lazy(() => import('./LogsAuditoria'));
const Sincronizacoes       = lazy(() => import('./Sincronizacoes'));
const Indicadores          = lazy(() => import('./Indicadores'));
const IndicadoresLideres   = lazy(() => import('./IndicadoresLideres'));

const ControleEntrada       = lazy(() => import('./ControleEntrada'));
const ControleProcessamento = lazy(() => import('./ControleProcessamento'));
const ControleSaidas        = lazy(() => import('./ControleSaidas'));
const Documentos            = lazy(() => import('./Documentos'));
const RelatorioComissoes    = lazy(() => import('./RelatorioComissoes'));
const RelatorioFinanceiro   = lazy(() => import('./RelatorioFinanceiro'));
const RelatorioNFPEV        = lazy(() => import('./RelatorioNFPEV'));
const RelatorioEntradasEstoque = lazy(() => import('./RelatorioEntradasEstoque'));
const RelatorioManifestos      = lazy(() => import('./RelatorioManifestos'));
const DashboardEstoque         = lazy(() => import('./DashboardEstoque'));
const DashboardSaidas           = lazy(() => import('./DashboardSaidas'));
const FaturasSaida              = lazy(() => import('./FaturasSaida'));
const DashboardEntradas         = lazy(() => import('./DashboardEntradas'));
const DashboardProcessamentos   = lazy(() => import('./DashboardProcessamentos'));
const DesmontagemColaboradores  = lazy(() => import('./DesmontagemColaboradores'));
const EstudoMaterial            = lazy(() => import('./EstudoMaterial'));
const HistoricoPrecos           = lazy(() => import('./HistoricoPrecos'));



// ─── Tela de Login ────────────────────────────────────────────────────────────
function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const motivo = sessionStorage.getItem('siscodel_logout_motivo');
    if (!motivo) return;
    sessionStorage.removeItem('siscodel_logout_motivo');
    setNotice(MOTIVO_LOGOUT_LABELS[motivo] || null);
  }, []);

  const handleLogin = async () => {
    if (!email || !password) return;
    setError('');
    setNotice(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) setError('E-mail ou senha incorretos. Tente novamente.');
    } catch {
      setError('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-overlay">
      <div className="login-card">
        <div className="login-header">
          <div className="header-logo" style={{ margin: '0 auto 16px' }}>
            <LayoutGrid size={22} />
          </div>
          <h1>SisCodel</h1>
          <p>Gestão de Material</p>
        </div>
        <div className="login-form">
          <div className="form-group">
            <label className="label">E-mail</label>
            <input
              type="text"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="email"
              className="input"
              placeholder="seu@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
          </div>
          <div className="form-group">
            <label className="label">Senha</label>
            <input
              type="password"
              autoComplete="current-password"
              className="input"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
          </div>
          {notice && !error && (
            <div className="login-error">
              <AlertTriangle size={15} /> {notice}
            </div>
          )}
          {error && (
            <div className="login-error">
              <AlertTriangle size={15} /> {error}
            </div>
          )}
          <button
            className="btn btn-primary btn-full"
            disabled={loading || !email || !password}
            onClick={handleLogin}
          >
            {loading ? <><div className="spinner-sm" /> Entrando...</> : <><Lock size={15} /> Entrar</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tela de Seleção de Módulo ────────────────────────────────────────────────
function ModuleSelector({ user, onSelect, onLogout, perfil }) {
  return (
    <div className="login-overlay" style={{ flexDirection: 'column', gap: 24 }}>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div className="header-logo" style={{ margin: '0 auto 16px', width: 56, height: 56, background: '#f1f5f9', border: '1.5px solid #cbd5e1', color: '#475569', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
          <LayoutGrid size={28} />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>SisCodel · Menu Principal</h2>
        <p style={{ fontSize: 13, color: 'var(--text3)', fontFamily: "'IBM Plex Mono', monospace" }}>Selecione o módulo desejado</p>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 960 }}>
        {/* Card: Entradas */}
        {moduloPermitido('entrada', perfil) && <button
          onClick={() => onSelect('entrada')}
          className="module-card"
          style={{
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
            padding: '28px 32px', width: 280, cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s', boxShadow: 'var(--shadow)',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green2)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(34,197,94,0.12)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--green-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--green)' }}>
            <Package size={24} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Entradas</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>Gestão de recebimento de materiais, pesagem, pendências e pagamentos.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--green)', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
            Acessar <ArrowRight size={15} />
          </div>
        </button>}

        {/* Card: Processamentos */}
        {moduloPermitido('ordens_servico', perfil) && <button
          onClick={() => onSelect('ordens_servico')}
          className="module-card"
          style={{
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
            padding: '28px 32px', width: 280, cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s', boxShadow: 'var(--shadow)',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(29,78,216,0.12)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--blue-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--blue)' }}>
            <Cpu size={24} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Processamentos</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>Gestão de desmonte, O.S., reciclagem e status de processamento.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--blue)', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
            Acessar <ArrowRight size={15} />
          </div>
        </button>}

        {/* Card: Saídas */}
        {moduloPermitido('saida', perfil) && <button
          onClick={() => onSelect('saida')}
          className="module-card"
          style={{
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
            padding: '28px 32px', width: 280, cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s', boxShadow: 'var(--shadow)',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--purple)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(124,58,237,0.12)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--purple-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--purple)' }}>
            <Truck size={24} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Saídas</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>Gestão de saídas de material, parceiros, MTR, NF e ordens de destinação.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--purple)', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
            Acessar <ArrowRight size={15} />
          </div>
        </button>}

        {/* Card: Documentos */}
        {moduloPermitido('documentos', perfil) && <button
          onClick={() => onSelect('documentos')}
          className="module-card"
          style={{
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
            padding: '28px 32px', width: 280, cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s', boxShadow: 'var(--shadow)',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#0891b2'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(8,145,178,0.12)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 10, background: '#ecfeff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0891b2' }}>
            <FolderOpen size={24} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Documentos</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>Gestão de validade de licenças, documentos Codel, parceiros e códigos IBAMA.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0891b2', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
            Acessar <ArrowRight size={15} />
          </div>
        </button>}

        {/* Card: Estoque */}
        {moduloPermitido('estoque_recipientes', perfil) && <button
          onClick={() => onSelect('estoque_recipientes')}
          className="module-card"
          style={{
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
            padding: '28px 32px', width: 280, cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s', boxShadow: 'var(--shadow)',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(245,158,11,0.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
            <Warehouse size={24} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Estoque</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>Gestão de estoque por prédios, corredores e prateleiras com mapa visual.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#d97706', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
            Acessar <ArrowRight size={15} />
          </div>
        </button>}

        {/* Card: Parceiros */}
        {moduloPermitido('parceiros_lista', perfil) && <button
          onClick={() => onSelect('parceiros_lista')}
          className="module-card"
          style={{
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
            padding: '28px 32px', width: 280, cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s', boxShadow: 'var(--shadow)',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#0f766e'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(15,118,110,0.12)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f0fdfa', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f766e' }}>
            <Users size={24} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Parceiros</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>Cadastro de clientes, fornecedores e parceiros com busca automática por CNPJ e CEP.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0f766e', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
            Acessar <ArrowRight size={15} />
          </div>
        </button>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <span className="user-email">{user?.email}</span>
        <button className="btn btn-ghost btn-logout" onClick={onLogout}>
          <LogOut size={14} /> Sair
        </button>
      </div>
    </div>
  );
}


// ─── App Principal ────────────────────────────────────────────────────────────
export default function App() {
  // Rota pública de rastreabilidade — sem autenticação (deve ficar antes dos hooks mas sem return antecipado)
  const _rastreioPath = window.location.pathname;
  const _isRastreio = _rastreioPath.startsWith('/rastreio/');
  const _codigoRastreio = _isRastreio ? _rastreioPath.replace('/rastreio/', '').split('/')[0] : null;

  const [session, setSession] = useState(undefined);
  const [perfil, setPerfil] = useState(undefined);
  const [modulo, setModulo] = useState(null);
  const [osIdParaAbrir, setOsIdParaAbrir] = useState(null);
  // Parâmetros de deep-link lidos UMA única vez via estado — nunca re-lidos
  const [urlOsId]      = useState(() => { const p = new URLSearchParams(window.location.search); return p.get('os_id') ? Number(p.get('os_id')) : null; });
  const [urlEntradaId] = useState(() => { const p = new URLSearchParams(window.location.search); return p.get('entrada_id') || null; });
  // Controla abertura automática de entrada via deep-link — zerado após primeiro uso
  // eslint-disable-next-line no-unused-vars
  const [entradaIdParaAbrir, setEntradaIdParaAbrir] = useState(null);

  // true quando o signOut em curso já tem motivo explicado (handleLogout já
  // gravou 'inatividade'/'horario', ou foi um clique manual em "Sair" — ~35
  // telas chamam supabase.auth.signOut() direto, sem passar por handleLogout,
  // então detectamos o clique pela classe do botão em vez de instrumentar
  // cada uma). Se o SIGNED_OUT chegar com isso false, a sessão caiu sozinha
  // (token inválido/expirado) — ninguém pediu esse logout.
  const logoutTratadoRef = useRef(false);

  useEffect(() => {
    const marcarLogoutManual = (e) => {
      if (e.target.closest('.btn-logout, .nav-drawer-signout')) logoutTratadoRef.current = true;
    };
    document.addEventListener('click', marcarLogoutManual, true);
    return () => document.removeEventListener('click', marcarLogoutManual, true);
  }, []);

  useEffect(() => {
    // Busca a sessão inicial
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session ?? null);
      // Após login, se havia parâmetro de URL, navega direto para o módulo correto
      if (session) {
        if (urlOsId)           { setOsIdParaAbrir(urlOsId); setModulo('ordens_servico'); }
        else if (urlEntradaId) { setEntradaIdParaAbrir(urlEntradaId); setModulo('entrada'); }
      }
    });

    // Listener de mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setSession(session);
      } else if (event === 'SIGNED_OUT') {
        if (!logoutTratadoRef.current) {
          sessionStorage.setItem('siscodel_logout_motivo', 'sessao_expirada');
          // Melhor esforço — nesse ponto o client já pode ter derrubado o token,
          // e a gravação exige auth.uid(). Sem garantia, mas não custa tentar.
          supabase.rpc('fn_log_auditoria_sessao', { p_motivo: 'sessao_expirada' }).catch(() => {});
        }
        logoutTratadoRef.current = false;
        setSession(null);
        setModulo(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Busca o perfil (role + módulos permitidos) uma única vez por usuário —
  // não a cada TOKEN_REFRESHED, que gera um novo objeto session com o mesmo user.id
  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) { setPerfil(undefined); return; }
    let cancelado = false;
    carregarPerfil(uid).then(p => { if (!cancelado) setPerfil(p); });
    return () => { cancelado = true; };
  }, [session?.user?.id]);

  // Guard central de acesso — bloqueia módulo fora do perfil, inclusive quando
  // vindo de deep-link (?os_id=, ?entrada_id=), que não passa pelo menu
  useEffect(() => {
    if (perfil === undefined) return;
    if (modulo && !moduloPermitido(modulo, perfil)) setModulo(null);
  }, [modulo, perfil]);

  const handleLogout = async (motivo) => {
    logoutTratadoRef.current = true;
    if (motivo) {
      sessionStorage.setItem('siscodel_logout_motivo', motivo);
      // Best-effort: falha de rede aqui não pode impedir o logout de acontecer.
      try { await supabase.rpc('fn_log_auditoria_sessao', { p_motivo: motivo }); } catch { /* diagnóstico, não bloqueante */ }
    }
    await supabase.auth.signOut();
    setModulo(null);
  };

  // Desloga automaticamente por inatividade (60min) ou fora do horário
  // liberado para o perfil (restrito/controlado com horário configurado)
  useSessaoGuard(!!session && perfil !== undefined, perfil, handleLogout);

  if (_isRastreio) return (
    <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}><div className="spinner" /></div>}>
      <RastreioPublico codigo={_codigoRastreio} />
    </Suspense>
  );

  if (session === undefined) return <div className="login-overlay" />;
  if (!session) return <LoginScreen />;
  // Perfil ainda carregando — evita "piscar" o menu completo antes de filtrar por acesso
  if (perfil === undefined) return <div className="login-overlay"><div className="spinner" /></div>;
  if (!modulo) return (
    <>
      <Suspense fallback={null}>
        <NavDrawer modulo={null} onNavigate={setModulo} session={session} perfil={perfil} />
      </Suspense>
      <ModuleSelector user={session.user} onSelect={setModulo} onLogout={handleLogout} perfil={perfil} />
    </>
  );

  // Fallback exibido enquanto os módulos lazy carregam
  const LoadingFallback = () => (
    <div className="login-overlay">
      <div className="spinner" />
    </div>
  );

  // NavDrawer global — renderizado sobre qualquer tela de módulo
  const DrawerGlobal = () => (
    <Suspense fallback={null}>
      <NavDrawer modulo={modulo} onNavigate={setModulo} session={session} perfil={perfil} />
    </Suspense>
  );

  if (modulo === 'entrada') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <ControleEntrada session={session} perfil={perfil} onBack={() => setModulo(null)} onAbrirOS={(osId) => { setOsIdParaAbrir(osId); setModulo('ordens_servico'); }} initialEntradaId={urlEntradaId} />
    </Suspense></>
  );
  if (modulo === 'dashboard_entradas') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <DashboardEntradas session={session} perfil={perfil} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'convenios') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <ConveniosApp session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'notas_fiscais') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <NotasFiscaisProprias session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'atendimentos') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <ControleAtendimentos session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'notas_servico') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <NotasServico session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'fin_custo_operacao') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <CustoOperacao session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'fin_dre') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <FinDRE session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'fin_contas_bancos') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <FinContasBancos session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'fin_fluxo_caixa') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <FluxoCaixaProjetado session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'fin_relatorio_contabil') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <RelatorioContabilidade session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'fin_conferencia_notas') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <RelatorioConferenciaNotas session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'indicadores') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <Indicadores session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'indicadores_lideres') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <IndicadoresLideres session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'fin_receitas_despesas') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <FinReceitasDespesas session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'fin_plano_contas') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <FinPlanoContas session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'guia_plano_contas') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <GuiaPlanoContas session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'caixa') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <ControleCaixa session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'parceiros_lista') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <Parceiros session={session} perfil={perfil} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'parceiros_crm') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <CRMParceiros session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'conta_colaboradores') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <ContaColaboradores session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'processamento') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <ControleProcessamento session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'dashboard_processamentos') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <DashboardProcessamentos session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'ordens_servico') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <OrdensServico session={session} onBack={() => setModulo(null)} initialOsId={osIdParaAbrir} />
    </Suspense></>
  );
  if (modulo === 'composicao_produto') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <ComposicaoProduto session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'desmontagem_colaboradores') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <DesmontagemColaboradores session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'estudo_material') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <EstudoMaterial session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'saida') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <ControleSaidas session={session} perfil={perfil} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'dashboard_saidas') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <DashboardSaidas session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'faturas_saida') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <FaturasSaida session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'documentos') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <Documentos session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'acondicionamento') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <Acondicionamento session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'estoque_recipientes') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <EstoqueRecipientes session={session} onBack={() => setModulo(null)} />
    </Suspense></>
  );
  if (modulo === 'relatorio_comissoes') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <RelatorioComissoes onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );
  if (modulo === 'relatorio_financeiro') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <RelatorioFinanceiro onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );
  if (modulo === 'relatorio_nf_pev') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <RelatorioNFPEV onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );
  if (modulo === 'relatorio_entradas_estoque') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <RelatorioEntradasEstoque onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );
  if (modulo === 'historico_precos') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <HistoricoPrecos onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );
  if (modulo === 'mtr_painel') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <RelatorioManifestos onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );
  if (modulo === 'dashboard_estoque') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <DashboardEstoque onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );
  if (modulo === 'controle_acesso') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <ControleAcesso onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );
  if (modulo === 'logs_auditoria') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <LogsAuditoria onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );
  if (modulo === 'sincronizacoes') return (
    <><DrawerGlobal /><Suspense fallback={<LoadingFallback />}>
      <Sincronizacoes onBack={() => setModulo(null)} session={session} />
    </Suspense></>
  );

  return null;
}
